import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Users, UserCheck, Home, MoreVertical, Archive, Eye, Edit, MessageSquare } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const TYPE_LABELS = {
  PARTICIPANT_TEAM: 'Participant', SIL_HOUSE_TEAM: 'SIL House', ACCOMMODATION_TEAM: 'Accommodation',
  SUPPORT_COORDINATION: 'Support Coord', CLINICAL_TEAM: 'Clinical', ROSTERING_TEAM: 'Rostering', CUSTOM: 'Custom',
};
const STATUS_COLORS = { ACTIVE: 'default', INACTIVE: 'secondary', ARCHIVED: 'outline' };

export default function TeamsListPage() {
  const [teams, setTeams] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, pageSize: 20 });
      if (typeFilter) params.set('team_type', typeFilter);
      const { data } = await api.get(`/teams?${params}`);
      setTeams(data.items || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load teams'); }
    setLoading(false);
  }, [page, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const handleArchive = async (id) => {
    try { await api.post(`/teams/${id}/archive`); toast.success('Team archived'); load(); }
    catch { toast.error('Failed to archive'); }
  };

  const filtered = teams.filter(t =>
    !search || t.team_name?.toLowerCase().includes(search.toLowerCase()) || t.team_code?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="teams-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Users className="w-6 h-6" />Teams</h1>
          <p className="text-muted-foreground mt-1">{total} teams</p>
        </div>
        <Button onClick={() => navigate('/teams/new')} data-testid="create-team-btn"><Plus className="w-4 h-4 mr-2" />Create Team</Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search teams..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="team-search" />
        </div>
        <Select value={typeFilter} onValueChange={v => { setTypeFilter(v === 'ALL' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="team-type-filter"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Types</SelectItem>
            {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? <div className="text-center py-12 text-muted-foreground">Loading...</div> :
      filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No teams found</p>
          <p className="text-sm mt-1">Create your first team to get started</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(t => (
            <Card key={t.id} className="hover:shadow-sm transition-shadow" data-testid={`team-row-${t.id}`}>
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1 cursor-pointer" onClick={() => navigate(`/teams/${t.id}`)}>
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    {t.team_type?.includes('HOUSE') || t.team_type?.includes('ACCOMMODATION') ? <Home className="w-5 h-5 text-primary" /> : <Users className="w-5 h-5 text-primary" />}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{t.team_name}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <Badge variant="outline" className="text-[10px]">{TYPE_LABELS[t.team_type] || t.team_type}</Badge>
                      <span><UserCheck className="w-3 h-3 inline mr-0.5" />{t.member_count || 0} members</span>
                      <span><Users className="w-3 h-3 inline mr-0.5" />{t.participant_count || 0} participants</span>
                      {t.leader_name && <span>Leader: {t.leader_name}</span>}
                      {t.branch_id && <span>{t.branch_id}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={STATUS_COLORS[t.status] || 'secondary'}>{t.status}</Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/teams/${t.id}`)}><Eye className="w-4 h-4 mr-2" />View</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/teams/${t.id}/edit`)}><Edit className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/teams/${t.id}?tab=messages`)}><MessageSquare className="w-4 h-4 mr-2" />Messages</DropdownMenuItem>
                      {t.status !== 'ARCHIVED' && <DropdownMenuItem onClick={() => handleArchive(t.id)} className="text-red-600"><Archive className="w-4 h-4 mr-2" />Archive</DropdownMenuItem>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {total > 20 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-sm text-muted-foreground">Page {page} of {Math.ceil(total / 20)}</span>
          <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
